import { useAuth } from '@/contexts/AuthContext';
import { BookOpen, Sparkles, Brain, GraduationCap } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTrialCopy, untilPhrase } from '@/lib/trial';

export default function LoginPage() {
  const { login } = useAuth();
  const trial = useTrialCopy();

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="login-page">
      {/* Hero Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="animate-slide-up w-full max-w-md">
          {/* Logo & Brand */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-emerald-400 shadow-xl shadow-blue-500/20 mb-6">
              <GraduationCap className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-3">
              Gurukul<span className="text-blue-500"> Veda</span>
            </h1>
            <p className="font-body text-base text-slate-500 leading-relaxed max-w-xs mx-auto">
              Your Personal tutor, always by your side. Ask anything, learn everything.
            </p>
          </div>

          {/* Login Card */}
          <div className="glass-panel rounded-3xl p-8 mb-8">
            <Button
              data-testid="login-google-button"
              onClick={login}
              className="w-full bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-full px-8 py-6 text-base font-heading font-medium shadow-sm hover:shadow-md transition-all duration-200 flex items-center justify-center gap-3"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Continue with Google
            </Button>
            <p className="text-center text-xs text-slate-500 mt-4 font-body">
              {trial.bannerEnabled !== false ? `${untilPhrase(trial)}. No credit card needed.` : 'No credit card needed.'}
            </p>
          </div>

          {/* Feature Highlights */}
          <div className="grid grid-cols-3 gap-3 stagger-children">
            {[
              { icon: Brain, label: 'AI Doubt Clearing', color: 'text-blue-500 bg-blue-50' },
              { icon: Sparkles, label: 'Smart Quizzes', color: 'text-emerald-500 bg-emerald-50' },
              { icon: BookOpen, label: 'Flashcards', color: 'text-amber-500 bg-amber-50' },
            ].map((f) => (
              <div key={f.label} className="flex flex-col items-center gap-2 p-3 rounded-2xl bg-white/60 border border-slate-100">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${f.color}`}>
                  <f.icon className="w-5 h-5" />
                </div>
                <span className="text-xs font-body text-slate-600 text-center leading-tight">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center py-4 px-6">
        <p className="text-xs text-slate-400 font-body">
          Built with care for every student who silently struggles
        </p>
      </div>
    </div>
  );
}
